<template>
  <v-card outlined class="mb-4 education-card">
    <v-card-text>
      <!-- ส่วนหัว -->
      <v-row align="center" class="mb-3">
        <v-col cols="auto">
          <v-icon color="teal darken-2" size="32">mdi-school</v-icon>
        </v-col>
        <v-col>
          <div class="text-h6 font-weight-bold teal--text text--darken-2">
            ข้อมูลการศึกษา
          </div>
        </v-col>
      </v-row>

      <!-- ส่วนการศึกษาหลัก -->
      <v-card outlined class="mb-4 education-main-card">
        <v-card-title class="subtitle-1 font-weight-bold teal--text">
          <v-icon left color="teal">mdi-certificate</v-icon>
          วุฒิการศึกษาหลัก (ที่ใช้สมัคร)
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- ระดับการศึกษา -->
            <v-col cols="12" md="4">
              <v-select
                v-model="localData.ed_abb_level"
                outlined
                dense
                label="ระดับการศึกษา"
                prepend-inner-icon="mdi-school-outline"
                :items="educationTypes"
                item-text="text"
                item-value="value"
                :rules="educationLevelRules"
                placeholder="เลือกระดับการศึกษา"
              >
                <template v-slot:selection="{ item }">
                  <v-chip small color="teal" text-color="white">
                    {{ item.text }}
                  </v-chip>
                </template>
              </v-select>

              <!-- ระบุอื่นๆ -->
              <v-text-field
                v-if="localData.ed_abb_level === 'อื่นๆ'"
                v-model="localData.ed_abb_level_etc"
                label="ระบุระดับการศึกษา"
                outlined
                dense
                prepend-inner-icon="mdi-pencil"
                :rules="[v => !!v || 'กรุณาระบุระดับการศึกษา']"
                placeholder="เช่น ประกาศนียบัตรบัณฑิต"
              />
            </v-col>

            <!-- วุฒิการศึกษา -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="localData.ed_abb"
                outlined
                dense
                label="วุฒิการศึกษา"
                prepend-inner-icon="mdi-certificate-outline"
                :rules="educationAbbRules"
                placeholder="เช่น ค.บ., บธ.บ."
                hint="ระบุคำย่อของวุฒิการศึกษา"
                persistent-hint
              >
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        small
                        color="info"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span>ตัวอย่าง: ค.บ., บธ.บ., วท.บ., ศศ.บ.</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-col>

            <!-- สาขาวิชา -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="localData.ed_name"
                outlined
                dense
                label="สาขาวิชา"
                prepend-inner-icon="mdi-book-open-variant"
                :rules="educationNameRules"
                placeholder="เช่น การศึกษาปฐมวัย"
                hint="ระบุชื่อสาขาวิชาที่สำเร็จการศึกษา"
                persistent-hint
              />
            </v-col>

            <!-- สถาบันการศึกษา -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="localData.ed_university"
                outlined
                dense
                label="สถาบันการศึกษาที่จบ"
                prepend-inner-icon="mdi-domain"
                :rules="universityRules"
                placeholder="ชื่อมหาวิทยาลัย/สถาบัน"
              />
            </v-col>

            <!-- วันที่เริ่มศึกษา -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="localData.ed_university_datebegin"
                outlined
                dense
                label="วันที่เริ่มศึกษา"
                prepend-inner-icon="mdi-calendar-start"
                type="date"
                :rules="dateBeginRules"
                :max="currentDate"
              />
            </v-col>

            <!-- วันที่สำเร็จการศึกษา -->
            <v-col cols="12" md="3">
              <v-text-field
                v-model="localData.ed_university_dateend"
                outlined
                dense
                label="วันที่สำเร็จการศึกษา"
                prepend-inner-icon="mdi-calendar-check"
                type="date"
                :rules="dateEndRules"
                :min="localData.ed_university_datebegin"
                :max="currentDate"
              />
            </v-col>

            <!-- แสดงระยะเวลาศึกษา -->
            <v-col cols="12" v-if="studyDuration">
              <v-alert
                dense
                outlined
                type="info"
                icon="mdi-clock-outline"
              >
                <div class="d-flex align-center">
                  <v-icon left>mdi-calendar-range</v-icon>
                  <div>
                    <span class="font-weight-bold">ระยะเวลาการศึกษา:</span>
                    <span class="ml-2">{{ studyDuration }}</span>
                  </div>
                </div>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-divider class="my-4" />

      <!-- ปริญญาโท -->
      <v-card outlined class="mb-4 master-degree-card">
        <v-card-title class="subtitle-1 font-weight-bold blue--text text--darken-2">
          <v-icon left color="blue darken-2">mdi-school</v-icon>
          การศึกษาระดับปริญญาโท (ถ้ามี)
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- ชื่อวุฒิปริญญาโท -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="localData.pt_ed_UpMaster"
                outlined
                dense
                label="ชื่อวุฒิการศึกษา ปริญญาโท"
                prepend-inner-icon="mdi-certificate"
                placeholder="เช่น ศษ.ม., บธ.ม."
                hint="ไม่บังคับ - กรอกถ้ามีวุฒิปริญญาโท"
                persistent-hint
              />
            </v-col>

            <!-- ไฟล์ปริญญาโท -->
            <v-col cols="12" md="5">
              <v-file-input
                v-model="masterFile"
                accept="application/pdf"
                outlined
                dense
                label="ปริญญาโท/หนังสือรับรอง (PDF)"
                prepend-icon="mdi-paperclip"
                prepend-inner-icon="mdi-file-pdf-box"
                :show-size="1000"
                :rules="optionalFileRules"
                placeholder="เลือกไฟล์ PDF"
                hint="อัปโหลดเอกสารรับรองการศึกษา"
                persistent-hint
                @change="handleMasterFileChange"
              >
                <template v-slot:selection="{ text }">
                  <v-chip small color="blue darken-2" text-color="white">
                    <v-icon left small>mdi-file-pdf-box</v-icon>
                    {{ text }}
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>

            <!-- วันที่สำเร็จการศึกษาปริญญาโท -->
            <v-col cols="12" md="3">
              <v-menu
                v-model="masterDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="localData.pt_ed_UpMasterDateTime"
                    outlined
                    dense
                    label="วันสำเร็จการศึกษา"
                    prepend-inner-icon="mdi-calendar-check"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    placeholder="เลือกวันที่"
                  >
                    <template v-slot:append>
                      <v-icon color="blue darken-2">mdi-calendar</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localData.pt_ed_UpMasterDateTime"
                  no-title
                  scrollable
                  locale="th"
                  :max="currentDate"
                  :min="localData.ed_university_dateend"
                  @input="masterDateMenu = false"
                >
                  <v-spacer />
                  <v-btn text color="primary" @click="masterDateMenu = false">
                    ปิด
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>

            <!-- แสดงข้อมูลไฟล์ที่เลือก (Master) -->
            <v-col cols="12" v-if="masterFile">
              <v-alert dense outlined type="success" icon="mdi-file-check">
                <v-row align="center">
                  <v-col>
                    <div class="font-weight-bold">
                      <v-icon small left>mdi-file-pdf-box</v-icon>
                      {{ masterFile.name }}
                    </div>
                    <div class="text-caption grey--text">
                      ขนาด: {{ formatFileSize(masterFile.size) }} | ประเภท: PDF
                    </div>
                  </v-col>
                  <v-col cols="auto">
                    <v-chip small color="success" outlined>
                      <v-icon small left>mdi-check</v-icon>
                      พร้อมอัปโหลด
                    </v-chip>
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ปริญญาเอก -->
      <v-card outlined class="doctor-degree-card">
        <v-card-title class="subtitle-1 font-weight-bold purple--text text--darken-2">
          <v-icon left color="purple darken-2">mdi-school</v-icon>
          การศึกษาระดับปริญญาเอก (ถ้ามี)
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- ชื่อวุฒิปริญญาเอก -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model="localData.pt_ed_UpDoctor"
                outlined
                dense
                label="ชื่อวุฒิการศึกษา ปริญญาเอก"
                prepend-inner-icon="mdi-certificate"
                placeholder="เช่น ปร.ด., Ph.D."
                hint="ไม่บังคับ - กรอกถ้ามีวุฒิปริญญาเอก"
                persistent-hint
              />
            </v-col>

            <!-- ไฟล์ปริญญาเอก -->
            <v-col cols="12" md="5">
              <v-file-input
                v-model="doctorFile"
                accept="application/pdf"
                outlined
                dense
                label="ปริญญาเอก/หนังสือรับรอง (PDF)"
                prepend-icon="mdi-paperclip"
                prepend-inner-icon="mdi-file-pdf-box"
                :show-size="1000"
                :rules="optionalFileRules"
                placeholder="เลือกไฟล์ PDF"
                hint="อัปโหลดเอกสารรับรองการศึกษา"
                persistent-hint
                @change="handleDoctorFileChange"
              >
                <template v-slot:selection="{ text }">
                  <v-chip small color="purple darken-2" text-color="white">
                    <v-icon left small>mdi-file-pdf-box</v-icon>
                    {{ text }}
                  </v-chip>
                </template>
              </v-file-input>
            </v-col>

            <!-- วันที่สำเร็จการศึกษาปริญญาเอก -->
            <v-col cols="12" md="3">
              <v-menu
                v-model="doctorDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="localData.pt_ed_UpDoctorDatetime"
                    outlined
                    dense
                    label="วันสำเร็จการศึกษา"
                    prepend-inner-icon="mdi-calendar-check"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    placeholder="เลือกวันที่"
                  >
                    <template v-slot:append>
                      <v-icon color="purple darken-2">mdi-calendar</v-icon>
                    </template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="localData.pt_ed_UpDoctorDatetime"
                  no-title
                  scrollable
                  locale="th"
                  :max="currentDate"
                  :min="localData.pt_ed_UpMasterDateTime || localData.ed_university_dateend"
                  @input="doctorDateMenu = false"
                >
                  <v-spacer />
                  <v-btn text color="primary" @click="doctorDateMenu = false">
                    ปิด
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>

            <!-- แสดงข้อมูลไฟล์ที่เลือก (Doctor) -->
            <v-col cols="12" v-if="doctorFile">
              <v-alert dense outlined type="success" icon="mdi-file-check">
                <v-row align="center">
                  <v-col>
                    <div class="font-weight-bold">
                      <v-icon small left>mdi-file-pdf-box</v-icon>
                      {{ doctorFile.name }}
                    </div>
                    <div class="text-caption grey--text">
                      ขนาด: {{ formatFileSize(doctorFile.size) }} | ประเภท: PDF
                    </div>
                  </v-col>
                  <v-col cols="auto">
                    <v-chip small color="success" outlined>
                      <v-icon small left>mdi-check</v-icon>
                      พร้อมอัปโหลด
                    </v-chip>
                  </v-col>
                </v-row>
              </v-alert>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- คำแนะนำการกรอกข้อมูล -->
      <v-expansion-panels flat class="mt-4">
        <v-expansion-panel>
          <v-expansion-panel-header color="teal lighten-5">
            <div>
              <v-icon small left color="teal darken-2">mdi-help-circle-outline</v-icon>
              <span class="text-caption font-weight-bold">
                คำแนะนำการกรอกข้อมูลการศึกษา
              </span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-bold mb-2">
                  📚 วุฒิการศึกษาหลัก
                </div>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon small color="teal">mdi-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        กรอกข้อมูลวุฒิการศึกษาที่ใช้ในการสมัครงาน
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon small color="teal">mdi-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        ระบุวุฒิการศึกษาตรงตามใบปริญญา/ใบรับรอง
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon small color="teal">mdi-check</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        ตรวจสอบความถูกต้องของชื่อสถาบันและสาขาวิชา
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-subtitle-2 font-weight-bold mb-2">
                  🎓 วุฒิการศึกษาเพิ่มเติม (โท/เอก)
                </div>
                <v-list dense>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon small color="blue">mdi-information</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        กรอกเฉพาะกรณีที่มีวุฒิปริญญาโทหรือเอก
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon small color="blue">mdi-information</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        อัปโหลดไฟล์ใบปริญญา/หนังสือรับรองการสำเร็จการศึกษา
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon>
                      <v-icon small color="blue">mdi-information</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">
                        ไฟล์ต้องเป็น PDF ขนาดไม่เกิน 5 MB
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "EducationSection",
  
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    educationTypes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      masterDateMenu: false,
      doctorDateMenu: false,
      masterFile: null,
      doctorFile: null,

      // กฎการตรวจสอบ - ข้อมูลหลัก (บังคับ)
      educationLevelRules: [
        v => !!v || "กรุณาเลือกระดับการศึกษา"
      ],

      educationAbbRules: [
        v => !!v || "กรุณากรอกวุฒิการศึกษา",
        v => (v && v.length >= 2) || "วุฒิการศึกษาต้องมีอย่างน้อย 2 ตัวอักษร",
        v => (v && v.length <= 20) || "วุฒิการศึกษาต้องไม่เกิน 20 ตัวอักษร"
      ],

      educationNameRules: [
        v => !!v || "กรุณากรอกสาขาวิชา",
        v => (v && v.length >= 3) || "ชื่อสาขาวิชาต้องมีอย่างน้อย 3 ตัวอักษร",
        v => (v && v.length <= 100) || "ชื่อสาขาวิชาต้องไม่เกิน 100 ตัวอักษร"
      ],

      universityRules: [
        v => !!v || "กรุณากรอกชื่อสถาบันการศึกษา",
        v => (v && v.length >= 5) || "ชื่อสถาบันต้องมีอย่างน้อย 5 ตัวอักษร",
        v => (v && v.length <= 200) || "ชื่อสถาบันต้องไม่เกิน 200 ตัวอักษร"
      ],

      dateBeginRules: [
        v => !!v || "กรุณาเลือกวันที่เริ่มศึกษา",
        v => this.validateDateBegin(v) || "วันที่เริ่มศึกษาไม่ถูกต้อง"
      ],

      dateEndRules: [
        v => !!v || "กรุณาเลือกวันที่สำเร็จการศึกษา",
        v => this.validateDateEnd(v) || "วันที่สำเร็จการศึกษาต้องหลังจากวันที่เริ่มศึกษา"
      ],

      // กฎการตรวจสอบไฟล์ (ไม่บังคับ)
      optionalFileRules: [
        v => !v || v.size < 5000000 || "ขนาดไฟล์ต้องไม่เกิน 5 MB",
        v => !v || v.type === "application/pdf" || "รองรับเฉพาะไฟล์ PDF เท่านั้น"
      ]
    };
  },

  computed: {
    localData: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },

    // วันที่ปัจจุบัน
    currentDate() {
      return new Date().toISOString().substr(0, 10);
    },

    // คำนวณระยะเวลาการศึกษา
    studyDuration() {
      if (!this.localData.ed_university_datebegin || !this.localData.ed_university_dateend) {
        return null;
      }

      const start = new Date(this.localData.ed_university_datebegin);
      const end = new Date(this.localData.ed_university_dateend);

      if (end <= start) return null;

      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);

      let result = [];
      if (years > 0) result.push(`${years} ปี`);
      if (months > 0) result.push(`${months} เดือน`);

      return result.join(' ') || 'น้อยกว่า 1 เดือน';
    }
  },

  methods: {
    // ตรวจสอบวันที่เริ่มศึกษา
    validateDateBegin(value) {
      if (!value) return true;
      return new Date(value) <= new Date();
    },

    // ตรวจสอบวันที่สำเร็จการศึกษา
    validateDateEnd(value) {
      if (!value || !this.localData.ed_university_datebegin) return true;
      
      const start = new Date(this.localData.ed_university_datebegin);
      const end = new Date(value);
      
      // วันที่สำเร็จต้องหลังวันเริ่มอย่างน้อย 1 ปี
      const oneYear = 365 * 24 * 60 * 60 * 1000;
      return (end - start) >= oneYear;
    },

    // จัดการการเปลี่ยนแปลงไฟล์ปริญญาโท
    handleMasterFileChange(file) {
      if (file) {
        if (file.size > 5000000) {
          this.$emit("file-error", "ขนาดไฟล์ปริญญาโทต้องไม่เกิน 5 MB");
          this.masterFile = null;
          return;
        }

        if (file.type !== "application/pdf") {
          this.$emit("file-error", "รองรับเฉพาะไฟล์ PDF เท่านั้น");
          this.masterFile = null;
          return;
        }

        this.$emit("master-file-change", file);
      }
    },

    // จัดการการเปลี่ยนแปลงไฟล์ปริญญาเอก
    handleDoctorFileChange(file) {
      if (file) {
        if (file.size > 5000000) {
          this.$emit("file-error", "ขนาดไฟล์ปริญญาเอกต้องไม่เกิน 5 MB");
          this.doctorFile = null;
          return;
        }

        if (file.type !== "application/pdf") {
          this.$emit("file-error", "รองรับเฉพาะไฟล์ PDF เท่านั้น");
          this.doctorFile = null;
          return;
        }

        this.$emit("doctor-file-change", file);
      }
    },

    // แปลงขนาดไฟล์
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  }
};
</script>

<style scoped>
.education-card {
  transition: all 0.3s ease;
  border-radius: 12px;
  border: 2px solid #e0f2f1;
}

.education-card:hover {
  box-shadow: 0 6px 24px rgba(0, 150, 136, 0.15);
  border-color: #80cbc4;
}

.education-main-card {
  border-radius: 10px;
  border: 2px solid #b2dfdb;
  background: linear-gradient(to bottom, #e0f2f1, #ffffff);
}

.master-degree-card {
  border-radius: 10px;
  border: 2px solid #bbdefb;
  background: linear-gradient(to bottom, #e3f2fd, #ffffff);
}

.doctor-degree-card {
  border-radius: 10px;
  border: 2px solid #e1bee7;
  background: linear-gradient(to bottom, #f3e5f5, #ffffff);
}

/* Custom styling */
::v-deep .v-text-field--outlined,
::v-deep .v-select--outlined {
  border-radius: 8px;
}

::v-deep .v-input__icon--prepend-inner i {
  color: #00897b !important;
}

::v-deep .v-alert {
  border-radius: 10px;
}

::v-deep .v-expansion-panel {
  border-radius: 8px !important;
  border: 1px solid #b2dfdb;
}

::v-deep .v-chip {
  border-radius: 8px !important;
}

/* Card title styling */
::v-deep .v-card__title {
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 12px;
  margin-bottom: 16px;
}
</style>